<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TwentyAPI from 'dashboard/api/integrations/twenty';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  personId: {
    type: String,
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const name = ref('');
const amount = ref('');
const currencyCode = ref('USD');
const stage = ref('NEW');
const isSubmitting = ref(false);
const errorMessage = ref('');

const currencies = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'IRR', label: 'IRR (Rial)' },
  { value: 'IRT', label: 'IRT (Toman)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
  { value: 'AED', label: 'AED (Dirham)' },
  { value: 'SAR', label: 'SAR (Riyal)' },
];

const stages = [
  { value: 'NEW', label: 'New / Lead' },
  { value: 'QUALIFIED', label: 'Qualified' },
  { value: 'PROPOSAL', label: 'Proposal Sent' },
  { value: 'WON', label: 'Closed Won' },
  { value: 'LOST', label: 'Closed Lost' },
];

const handleSubmit = async () => {
  if (!name.value.trim()) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    await TwentyAPI.createOpportunity({
      name: name.value,
      amount: amount.value ? parseFloat(amount.value) : null,
      currency_code: currencyCode.value,
      stage: stage.value,
      person_id: props.personId,
      conversation_id: props.conversationId,
      contact_id: props.contactId,
    });
    emit('created');
    emit('close');
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('INTEGRATION_SETTINGS.TWENTY.CREATE_OPPORTUNITY_ERROR', 'Failed to create opportunity');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
    <div class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
      <div class="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-base font-semibold text-slate-900 dark:text-white">
          {{ t('INTEGRATION_SETTINGS.TWENTY.ADD_OPPORTUNITY', 'Add Opportunity in Twenty CRM') }}
        </h3>
        <button
          type="button"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <form class="mt-4 space-y-4" @submit.prevent="handleSubmit">
        <div v-if="errorMessage" class="p-3 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">
          {{ errorMessage }}
        </div>

        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ t('INTEGRATION_SETTINGS.TWENTY.OPPORTUNITY_NAME', 'Opportunity Name') }} *
          </label>
          <input
            v-model="name"
            type="text"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
            placeholder="Enterprise Plan Deal"
          />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('INTEGRATION_SETTINGS.TWENTY.AMOUNT', 'Amount') }}
            </label>
            <input
              v-model="amount"
              type="number"
              step="0.01"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
              placeholder="5000"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('INTEGRATION_SETTINGS.TWENTY.CURRENCY', 'Currency') }}
            </label>
            <select
              v-model="currencyCode"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option v-for="c in currencies" :key="c.value" :value="c.value">
                {{ c.label }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ t('INTEGRATION_SETTINGS.TWENTY.STAGE', 'Stage') }}
          </label>
          <select
            v-model="stage"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option v-for="s in stages" :key="s.value" :value="s.value">
              {{ s.label }}
            </option>
          </select>
        </div>

        <div class="flex items-center justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            color="slate"
            :label="t('INTEGRATION_SETTINGS.TWENTY.CANCEL', 'Cancel')"
            @click="emit('close')"
          />
          <Button
            type="submit"
            color="blue"
            :is-loading="isSubmitting"
            :label="t('INTEGRATION_SETTINGS.TWENTY.CREATE_OPPORTUNITY_SUBMIT', 'Create Opportunity')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
